import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Code, Rocket, Star, Trophy, Users, Zap, Check, ChevronDown } from "lucide-react";

export const Route = createFileRoute("/")({
  component: LandingPage,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        title: "Code Cubs Academy — Fun coding classes for kids 6-16",
      },
      {
        name: "description",
        content:
          "Live, project-based coding classes for kids: Scratch, Python, Web Dev and AI. Small groups, expert mentors, parent dashboard.",
      },
      {
        property: "og:title",
        content: "Code Cubs Academy — Fun coding classes for kids 6-16",
      },
      {
        property: "og:description",
        content:
          "Live, project-based coding classes for kids: Scratch, Python, Web Dev and AI. Small groups, expert mentors, parent dashboard.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Code Cubs Academy — Fun coding classes for kids 6-16",
      },
      {
        name: "twitter:description",
        content:
          "Live, project-based coding classes for kids: Scratch, Python, Web Dev and AI. Small groups, expert mentors, parent dashboard.",
      },
    ],
  }),
});

// ─── Data ────────────────────────────────────────────────────────────────────

const courses = [
  {
    title: "Python Basics",
    level: "Beginner",
    age: "8-12",
    xp: 500,
    color: "from-orange-400 to-orange-600",
    icon: "🐍",
    students: 1234,
  },
  {
    title: "Web Development",
    level: "Intermediate",
    age: "10-14",
    xp: 750,
    color: "from-blue-400 to-blue-600",
    icon: "🌐",
    students: 987,
  },
  {
    title: "Game Development",
    level: "Advanced",
    age: "12-16",
    xp: 1000,
    color: "from-green-400 to-green-600",
    icon: "🎮",
    students: 654,
  },
  {
    title: "Scratch Coding",
    level: "Beginner",
    age: "6-10",
    xp: 300,
    color: "from-purple-400 to-purple-600",
    icon: "🎨",
    students: 2341,
  },
];

const achievements = [
  { name: "First Code", icon: "🌟", unlocked: true },
  { name: "Week Streak", icon: "🔥", unlocked: true },
  { name: "Bug Hunter", icon: "🐛", unlocked: true },
  { name: "Team Player", icon: "🤝", unlocked: false },
  { name: "Master Coder", icon: "👑", unlocked: false },
  { name: "Speed Runner", icon: "⚡", unlocked: false },
];

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "Parent of Emma, 10",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop",
    quote:
      "My daughter went from zero coding knowledge to building her own games in just 3 months!",
    rating: 5,
  },
  {
    name: "Michael Chen",
    role: "Parent of Lucas, 12",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop",
    quote: "The progress tracking is incredible. Highly recommend!",
    rating: 5,
  },
  {
    name: "Jessica Williams",
    role: "Parent of Olivia, 8",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop",
    quote: "Code Cubs makes learning fun!",
    rating: 5,
  },
];

const pricing = [
  {
    name: "Explorer",
    price: 29,
    period: "month",
    description: "Perfect for trying out coding",
    features: [
      "2 courses access",
      "5 hours/month instructor time",
      "Progress tracking",
      "Achievement badges",
      "Community access",
    ],
    color: "from-orange-400 to-orange-600",
    popular: false,
  },
  {
    name: "Adventurer",
    price: 49,
    period: "month",
    description: "Most popular for serious learners",
    features: [
      "All courses access",
      "10 hours/month instructor time",
      "Advanced progress analytics",
      "All achievement badges",
      "Priority community access",
      "Parent dashboard",
      "Monthly progress reports",
    ],
    color: "from-blue-400 to-blue-600",
    popular: true,
  },
  {
    name: "Champion",
    price: 89,
    period: "month",
    description: "Ultimate learning experience",
    features: [
      "Everything in Adventurer",
      "Unlimited instructor time",
      "1-on-1 mentoring sessions",
      "Custom learning paths",
      "Project showcases",
      "Certificate programs",
      "Early access to new courses",
    ],
    color: "from-green-400 to-green-600",
    popular: false,
  },
];

const faqs = [
  {
    q: "What age groups do you cater to?",
    a: "Code Cubs Academy offers courses for children aged 6-16.",
  },
  {
    q: "Do I need to have coding experience to help my child?",
    a: "Not at all! Our platform is designed to be self-guided with instructor support.",
  },
  {
    q: "What programming languages do you teach?",
    a: "We teach Python, JavaScript, HTML/CSS, Scratch, and more.",
  },
  {
    q: "Can my child work at their own pace?",
    a: "Absolutely! Our courses are self-paced.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes! We offer a 7-day free trial.",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-blue-600/10 to-emerald-600/20 dark:from-violet-900/30 dark:via-blue-900/20 dark:to-emerald-900/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(139,92,246,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.1),transparent_50%)]" />

        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="space-y-8"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700/50"
              >
                <Rocket className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                <span className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                  #1 Coding Platform for Kids
                </span>
              </motion.div>

              <h1 className="text-5xl md:text-7xl font-extrabold leading-[1.05] tracking-tight">
                <span className="text-foreground">Where Kids </span>
                <span className="bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent">
                  Learn to Code
                </span>
                <br />
                <span className="text-foreground">& </span>
                <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                  Build Dreams
                </span>
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                Interactive coding courses designed for ages 6-16. Learn Python, web development,
                game design and more through fun, project-based lessons.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white font-bold rounded-2xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Rocket className="w-5 h-5" />
                  Start Free Trial
                </Link>
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 border border-gray-200 dark:border-white/20 text-foreground font-bold rounded-2xl transition-all duration-300 hover:-translate-y-0.5"
                >
                  Explore Courses
                </Link>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 pt-4">
                {[
                  { value: "10K+", label: "Students" },
                  { value: "50+", label: "Courses" },
                  { value: "4.9★", label: "Rating" },
                ].map((stat) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="text-2xl font-extrabold text-foreground">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Floating achievement cards */}
            <div className="relative hidden lg:block">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="relative"
              >
                {/* Main code card */}
                <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-700/50 p-8 shadow-2xl">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="ml-auto text-xs text-muted-foreground font-mono">
                      my_game.py
                    </span>
                  </div>
                  <pre className="font-mono text-sm leading-relaxed">
                    <code>
                      <span className="text-violet-600 dark:text-violet-400">class</span>{" "}
                      <span className="text-blue-600 dark:text-blue-400">Game</span>:{"\n"}
                      {"  "}
                      <span className="text-violet-600 dark:text-violet-400">def</span>{" "}
                      <span className="text-emerald-600 dark:text-emerald-400">__init__</span>
                      (self):{"\n"}
                      {"    "}self.score ={" "}
                      <span className="text-orange-600 dark:text-orange-400">0</span>
                      {"\n"}
                      {"    "}self.level ={" "}
                      <span className="text-orange-600 dark:text-orange-400">1</span>
                      {"\n\n"}
                      {"  "}
                      <span className="text-violet-600 dark:text-violet-400">def</span>{" "}
                      <span className="text-emerald-600 dark:text-emerald-400">level_up</span>
                      (self):{"\n"}
                      {"    "}self.level +={" "}
                      <span className="text-orange-600 dark:text-orange-400">1</span>
                      {"\n"}
                      {"    "}print(
                      <span className="text-emerald-600 dark:text-emerald-400">"🎉 Level Up!"</span>
                      )
                    </code>
                  </pre>
                </div>

                {/* Floating achievement badge - top right */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute -top-6 -right-6 bg-gradient-to-br from-yellow-400 to-orange-500 text-white p-4 rounded-2xl shadow-lg"
                >
                  <Trophy className="w-6 h-6 mb-1" />
                  <div className="text-xs font-bold">Level 5!</div>
                </motion.div>

                {/* Floating XP card - bottom left */}
                <motion.div
                  animate={{ y: [0, 6, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                  }}
                  className="absolute -bottom-4 -left-8 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-blue-500 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">XP Earned</div>
                      <div className="font-bold text-emerald-600 dark:text-emerald-400">
                        +250 XP
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating streak card - right */}
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5,
                  }}
                  className="absolute top-1/2 -right-12 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-3"
                >
                  <div className="text-2xl">🔥</div>
                  <div className="text-xs font-bold mt-1">7 Day Streak</div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ COURSES ═══════════════════ */}
      <section className="py-24 bg-gray-50/50 dark:bg-gray-900/50">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-semibold mb-4">
              <Code className="w-4 h-4" />
              Popular Courses
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Learn by Building{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                Real Projects
              </span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Hands-on courses designed to spark creativity and build real-world skills.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map((course, index) => (
              <motion.div
                key={course.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="group bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-6 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50 transition-shadow duration-300"
              >
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${course.color} flex items-center justify-center text-2xl mb-5 shadow-lg`}
                >
                  {course.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{course.title}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-muted-foreground">
                    {course.level}
                  </span>
                  <span className="text-xs text-muted-foreground">Ages {course.age}</span>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    {course.students.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                    <Zap className="w-4 h-4" />
                    {course.xp} XP
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ ACHIEVEMENTS ═══════════════════ */}
      <section className="py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-sm font-semibold mb-4">
              <Trophy className="w-4 h-4" />
              Achievements
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Earn Badges &{" "}
              <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
                Level Up
              </span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Stay motivated with achievements, XP points, and weekly streaks.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            {achievements.map((badge, index) => (
              <motion.div
                key={badge.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08, duration: 0.4 }}
                whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
                className={`flex flex-col items-center p-5 rounded-2xl border transition-all duration-300 ${
                  badge.unlocked
                    ? "bg-gradient-to-b from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-yellow-200 dark:border-yellow-800/50 shadow-md"
                    : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 opacity-50"
                }`}
              >
                <span className="text-4xl mb-2">{badge.icon}</span>
                <span
                  className={`text-xs font-bold text-center ${
                    badge.unlocked ? "text-foreground" : "text-muted-foreground"
                  }`}
                >
                  {badge.name}
                </span>
                {badge.unlocked && (
                  <div className="mt-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ TESTIMONIALS ═══════════════════ */}
      <section className="py-24 bg-gray-50/50 dark:bg-gray-900/50">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-sm font-semibold mb-4">
              <Star className="w-4 h-4" />
              Testimonials
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Loved by{" "}
              <span className="bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
                Parents & Kids
              </span>
            </h2>
            <p className="text-lg text-muted-foreground">
              See what families are saying about Code Cubs Academy.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-foreground/90 leading-relaxed mb-6 italic">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-bold text-sm">{testimonial.name}</div>
                    <div className="text-xs text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PRICING ═══════════════════ */}
      <section className="py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm font-semibold mb-4">
              <Zap className="w-4 h-4" />
              Pricing
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Simple,{" "}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                Transparent
              </span>{" "}
              Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Choose the plan that&apos;s right for your young coder.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-start">
            {pricing.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className={`relative rounded-3xl border p-8 transition-all duration-300 ${
                  plan.popular
                    ? "bg-gradient-to-b from-blue-50 to-violet-50 dark:from-blue-950/50 dark:to-violet-950/50 border-blue-300 dark:border-blue-700 shadow-xl shadow-blue-200/30 dark:shadow-blue-900/30 scale-105 z-10"
                    : "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:shadow-lg"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-blue-600 to-violet-600 text-white text-xs font-bold rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-extrabold">${plan.price}</span>
                    <span className="text-muted-foreground">/{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          plan.popular
                            ? "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
                            : "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400"
                        }`}
                      >
                        <Check className="w-3 h-3" />
                      </div>
                      <span className="text-sm text-foreground/80">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`block w-full text-center py-3.5 rounded-xl font-bold text-sm transition-all duration-300 ${
                    plan.popular
                      ? "bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-lg shadow-blue-500/25"
                      : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-foreground"
                  }`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section className="py-24 bg-gray-50/50 dark:bg-gray-900/50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
              Frequently Asked{" "}
              <span className="bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent">
                Questions
              </span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about Code Cubs Academy.
            </p>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08, duration: 0.4 }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl px-6 py-5 text-left hover:border-violet-300 dark:hover:border-violet-700 transition-colors duration-300"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-semibold text-foreground">{faq.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <p className="pt-3 text-muted-foreground leading-relaxed text-sm">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ CTA ═══════════════════ */}
      <section className="py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-violet-600 via-blue-600 to-emerald-600 p-12 md:p-20 text-center shadow-2xl"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
            <div className="absolute top-6 right-8 font-mono text-8xl md:text-9xl opacity-[0.07] select-none pointer-events-none text-white">
              {"</>"}
            </div>
            <div className="absolute bottom-6 left-8 font-mono text-8xl md:text-9xl opacity-[0.07] select-none pointer-events-none text-white">
              {"{ }"}
            </div>

            <div className="relative z-10">
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm mb-6"
              >
                <Rocket className="w-8 h-8 text-white" />
              </motion.div>

              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
                Ready to Start Your
                <br />
                Coding Adventure?
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
                Join thousands of young coders already building amazing projects. Start your free
                7-day trial today!
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-violet-700 font-bold rounded-2xl shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Rocket className="w-5 h-5" />
                  Start Free Trial
                </Link>
                <Link
                  to="/courses"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/30 text-white font-bold rounded-2xl transition-all duration-300 hover:-translate-y-0.5"
                >
                  Explore Courses
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
